import { Component, Input, OnInit } from '@angular/core';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { DataTableBase } from 'src/app/core/base/components/data-table-base/data-table-base';
import { FeiColumn } from 'src/app/core/base/interfaces/fei-column.interface';
import { ReferenceContractsModel } from '../../../../../../core/models/reference-contracts.model';
import { ReferenceContractsService } from '../../../reference-contracts/reference-contracts.service';
import { ProductModel } from '../../../../../../core/models/product.model';
import { EstablishmentModel } from '../../../../../../core/models/establishment.model';
import { ProductService } from '../../../product/product.service';
import { debounceTime, switchMap } from 'rxjs/operators';

@Component({
  selector: 'vex-product-select',
  templateUrl: './product-select.component.html',
  styleUrls: ['./product-select.component.scss'],
})
export class ProductSelectComponent extends DataTableBase<ProductModel> implements OnInit {
  @Input() contracts: string[] = [];
  @Input() alreadySelected: ProductModel[] = [];
  @Input() supportEstablishment: EstablishmentModel;

  feisColumns: Array<FeiColumn> = [
    { label: '', column: 'selection' },
    { label: 'Fournisseur', column: 'supplier', propertyDisplay: 'supplier.label' },
    { label: 'Référence', column: 'reference', propertyDisplay: 'reference' },
    { label: 'Libellé', column: 'label', propertyDisplay: 'label' },
    { label: 'Article générique', column: 'genericProduct', propertyDisplay: 'genericProduct.label' },
    { label: 'UA', column: 'purchaseUnit' },
    { label: 'Conditionnement', column: 'conditioningUnit' },
    // { label: 'Prix TTC', column: 'priceTTC', propertyDisplay: 'priceTTC', type: 'number' }
  ];

  displayedColumns: string[] = this.feisColumns.map(c => c.column);

  dataSource = new MatTableDataSource<ProductModel>();
  /** Selected rows for the current page */
  selected: ProductModel[] = [];
  /** All selected products across pages */
  selectedProducts: ProductModel[] = [];

  constructor(public productsService: ProductService) {
    super();
    this.pageIndex = 1;
    this.pageSize = 10;
    this.sortField = 'label';
    this.sortOrder = 'desc';
  }

  ngOnInit() {
    this.filters = {
      contracts: this.contracts,
      excludedIds: this.alreadySelected?.map(p => p.initialObject),
      establishmentIdForNeeds: this.supportEstablishment?._id,
      isInternal: false,
      isSuspended: false,
      isContractValid: true,
      stockable: true,
    };
    this.subscription.add(
      this.pageChangeSubject
        .pipe(
          debounceTime(300),
          switchMap(({ pageIndex, pageSize }) => {
            this.pageIndex = pageIndex;
            this.pageSize = pageSize;
            return this.productsService.findAll(
              this.pageIndex,
              this.pageSize,
              this.sortField,
              this.sortOrder,
              this.search,
              { ...this.filters, requiredFields: this.requiredFields },
            );
          }),
        )
        .subscribe({
          next: res => {
            const transformedData = this.transformData(res.data);
            if (!this.dataSource) {
              this.dataSource = new MatTableDataSource(transformedData);
            } else {
              this.dataSource.data = transformedData;
            }
            this.count = res.totalData;
            this.loading = false;
          },
          error: () => {
            this.loading = false;
          },
        }),
    );

    this.initData();
  }

  protected override transformData(data: any[]): any[] {
    setTimeout(() => this.restoreSelection(), 0);
    return data;
  }

  onSearchChange(value: string) {
    super.onSearchChange(value);
  }

  onClearSearch() {
    super.onSearchChange('');
  }

  selectRow(rows: ProductModel[]) {
    const pageIds = this.dataSource.data.map(p => p._id);
    this.selectedProducts = this.selectedProducts.filter(p => !pageIds.includes(p._id));
    this.selectedProducts = [...this.selectedProducts, ...rows];
    this.selected = rows;
  }

  hasSelection(): boolean {
    return this.selectedProducts.length > 0;
  }

  myPageEvent(event: PageEvent) {
    super.pageEvent(event);
    setTimeout(() => this.restoreSelection(), 0);
  }

  restoreSelection() {
    if (!this.selectedProducts?.length) {
      this.selected = [];
      return;
    }
    const ids = this.selectedProducts.map(p => p._id);
    this.selected = this.dataSource.data.filter(p => ids.includes(p._id));
  }

  /** Select all results across all pages for current filters */
  selectAllResults() {
    if (!this.count || this.count <= 0) {
      return;
    }
    this.loading = true;
    this.productsService
      .findAll(1, this.count, this.sortField, this.sortOrder, this.search, { ...this.filters, requiredFields: ['_id'] })
      .subscribe({
        next: res => {
          this.selectedProducts = res.data as unknown as ProductModel[];
          // refresh current page checkboxes
          this.restoreSelection();
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  /** Clear all selections */
  clearAllSelections() {
    this.selectedProducts = [];
    this.selected = [];
  }
}
